import { createContext, use, useState } from "react";

import { cn } from "@bentolabs/kit/utils";
import { Tooltip as RadixTooltip } from "radix-ui";

import { useResponsive } from "shared/hooks/use-responsive";

type TooltipTriggerContextType = {
  open: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
};

const TooltipTriggerContext = createContext<TooltipTriggerContextType>({
  open: false,
  onOpenChange: () => {},
});

export const Tooltip = ({ children, ...props }: RadixTooltip.TooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { md } = useResponsive();

  return (
    <RadixTooltip.Provider>
      <RadixTooltip.Root
        delayDuration={md ? props.delayDuration : 0}
        open={isOpen}
        onOpenChange={setIsOpen}
        {...props}
      >
        <TooltipTriggerContext
          value={{ open: isOpen, onOpenChange: setIsOpen }}
        >
          {children}
        </TooltipTriggerContext>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

const Content = ({
  children,
  className,
  ...props
}: RadixTooltip.TooltipContentProps) => {
  return (
    <RadixTooltip.Portal>
      <RadixTooltip.Content
        {...props}
        className={cn(
          "TooltipContent",
          "text-nobel bg-onyx/80 z-50 rounded-xl border border-white/5 px-3 py-2 text-xs leading-[150%] whitespace-pre-line backdrop-blur-md",
          className
        )}
      >
        {children}
        <RadixTooltip.Arrow />
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
};

const Trigger = ({ className, ...props }: RadixTooltip.TooltipTriggerProps) => {
  const { md } = useResponsive();
  const { onOpenChange } = use(TooltipTriggerContext);
  return (
    <RadixTooltip.Trigger
      {...props}
      onClick={(e) => {
        if (!md) e.preventDefault();
        onOpenChange(true);
      }}
      className={cn("cursor-pointer", className)}
    />
  );
};

Tooltip.Trigger = Trigger;
Tooltip.Content = Content;
